import React from "react";
import { NavLink } from "react-router-dom";
import { useVendorForm } from "../VendorFormContext";
import { toast } from "react-toastify";

/**
 * VendorSubNav - A validation-aware navigation component for main vendor sections
 *
 * In create mode (isCreating=true):
 * - Summary is always accessible
 * - Details is locked until all Summary tabs are completed
 * - Files is locked until all Summary and Details tabs are completed
 *
 * In edit mode (isCreating=false):
 * - All sections are accessible freely
 */
const VendorSubNav = ({ name, link }) => {
  const { isCreating, canAccessSection, isSummaryComplete, isDetailsComplete, completedTabs } = useVendorForm();

  const isAccessible = canAccessSection(link);

  // Determine completion status for visual indicator
  const getSectionCompletionStatus = () => {
    if (!isCreating) return 'complete';

    switch (link) {
      case 'summary':
        return isSummaryComplete() ? 'complete' : 'in-progress';
      case 'details':
        if (!isSummaryComplete()) return 'locked';
        return isDetailsComplete() ? 'complete' : 'in-progress';
      case 'files':
        if (!isSummaryComplete() || !isDetailsComplete()) return 'locked';
        return 'accessible';
      default:
        return 'locked';
    }
  };

  const completionStatus = getSectionCompletionStatus();

  const handleClick = (e) => {
    if (isCreating && !isAccessible) {
      e.preventDefault();

      // Provide specific message based on which section is locked
      if (link === 'details') {
        toast.warning("Please complete all Summary tabs before accessing Details", {
          toastId: "section-validation-warning",
        });
      } else if (link === 'files') {
        if (!isSummaryComplete()) {
          toast.warning("Please complete all Summary tabs first", {
            toastId: "section-validation-warning",
          });
        } else {
          toast.warning("Please complete all Details tabs before accessing Files", {
            toastId: "section-validation-warning",
          });
        }
      }
      return;
    }
  };

  // Base styles
  const baseStyles = "p-2 rounded-tl-lg rounded-tr-lg justify-start items-center flex text-sm font-normal leading-tight tracking-tight gap-1 transition-all duration-200";

  // Active styles
  const activeStyles = "bg-primary-500 text-white";

  // Inactive but accessible styles
  const inactiveStyles = "text-neutral-500 hover:text-primary-400";

  // Disabled/locked styles
  const disabledStyles = "text-gray-400 cursor-not-allowed opacity-50";

  return (
    <NavLink
      to={link}
      onClick={handleClick}
      className={({ isActive }) => {
        if (isCreating && !isAccessible) {
          return `${baseStyles} ${disabledStyles}`;
        }
        return isActive
          ? `${baseStyles} ${activeStyles}`
          : `${baseStyles} ${inactiveStyles}`;
      }}
    >
      {/* Show status indicator in create mode */}
      {isCreating && completionStatus === 'complete' && (
        <span className="text-green-500 text-xs">&#10003;</span>
      )}
      {name}
      {/* Show lock icon for inaccessible sections in create mode */}
      {isCreating && !isAccessible && (
        <span className="text-gray-400 text-xs ml-1">&#128274;</span>
      )}
    </NavLink>
  );
};

export default VendorSubNav;
